import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { css } from "styled-components";

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    defaultProductImage?: InjectableCss;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        css: css`
            text-align: left;
            margin: 0 0 16px 0;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: 16px;
            color: ${getProp("theme.typography.p.color")};
            text-transform: uppercase;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            // margin: 5px 0;
            cursor: pointer;
            &:not(:last-child) {
                padding-bottom: 16px;
            }
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            // margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
            padding-right: 10px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
                color: ${COLORS.background.tag} !important;
                font-weight: ${TYPOGRAPHY.weight.base};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: 20px;
                > div {
                    white-space: nowrap;
                    width: 155px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    @media (max-width: 767px) {
                        width: 200px;
                    }
                    @media (min-width: 768px) and (max-width: 991px) {
                        width: 260px;
                    }
                    @media (min-width: 992px) and (max-width: 1200px) {
                        width: 70px;
                    }
                    @media (min-width: 1201px) and (max-width: 1300px) {
                        width: 80px;
                    }
                    @media (min-width: 1301px) {
                        width: 130px;
                    }
                }
            `,
        },
        css: css`
            // width: 100%;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            text-align: left;
            margin: 0 0 16px 0;
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: ${TYPOGRAPHY.size.tiny};
            line-height: 16px;
            color: ${COLORS.background.tag};

            > div {
                white-space: nowrap;
                width: 145px;
                overflow: hidden;
                text-overflow: ellipsis;
                @media (max-width: 767px) {
                    width: 200px;
                }
                @media (min-width: 768px) and (max-width: 991px) {
                    width: 260px;
                }
                @media (min-width: 992px) and (max-width: 1200px) {
                    width: 70px;
                }
                @media (min-width: 1201px) and (max-width: 1300px) {
                    width: 80px;
                }
                @media (min-width: 1301px) {
                    width: 135px;
                }
            }
        `,
    },
    defaultProductImage: {
        css: css`
            background: #fff;
            justify-content: center;
            img {
                width: 100%;
                max-height: 55px;
                object-fit: contain;
                height: auto;
                aspect-ratio: 1/1;
            }
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;
        return (
            <>
                <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteProducts_Header"
                    className="autocomplete-products-header"
                >
                    {translate("Products")}
                </Typography>
                {products.map(product => (
                    <GridContainer
                        {...(this.props.focusedItem === product ? styles.focusedContainer : styles.container)}
                        key={product.id!}
                        onClick={() => {
                            this.props.goToUrl(product.url);
                        }}
                        className="autocomplete-products-container"
                    >
                        <GridItem {...styles.imageGridItem} className="autocomplete-products-image-grid-item">
                            {product.image ? (
                                <LazyImage
                                    {...styles.image}
                                    src={product.image}
                                    className="autocomplete-products-image"
                                />
                            ) : (
                                <Clickable
                                    href={product.properties.productDetailPath}
                                    data-test-selector="productImage"
                                    {...styles.defaultProductImage}
                                >
                                    <LazyImage
                                        src="/userFiles/images/default-product-image.png"
                                        altText="Default product image"
                                    />
                                </Clickable>
                            )}
                        </GridItem>

                        <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                            <Link
                                {...styles.titleLink}
                                data-test-selector={`productId_${product.id}`}
                                className="autocomplete-products-link"
                            >
                                <div>{product.displayTitle}</div>
                            </Link>
                            <Typography
                                {...styles.erpNumberText}
                                data-test-selector="autocompleteProducts_PartNumber"
                                className="autocomplete-products-erp-number"
                            >
                                <div>
                                    {product.isNameCustomerOverride ? (
                                        <>
                                            {translate("My Part #")}
                                            {product.displayName}
                                        </>
                                    ) : (
                                        <>
                                            {translate("Part #")}
                                            {product.displayErpNumber}
                                        </>
                                    )}
                                    {product.manufacturerItemNumber && (
                                        <>
                                            &nbsp;
                                            {translate("MFG #")}
                                            {product.displayManufacturerItemNumber}
                                        </>
                                    )}
                                </div>
                            </Typography>
                        </GridItem>
                    </GridContainer>
                ))}
            </>
        );
    }
}

export default AutocompleteProducts;
